import { ChangeEventHandler, Fragment, MouseEventHandler, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormInput } from '../components/custom/components'
import PageTitle from '../components/custom/components/PageTitle'
import VerticalLayout from '../components/custom/layouts/Vertical'
import Modal from '../components/Modal'
import Option from '../components/Option'
import { ToastTypes, useToast } from '../components/toast/ToastProvider'
import { FormType, FormTypeList } from '../components/types'
import VehicleServiceForm from '../components/VehicleServiceForm'
import api, { APIResponse, getAxiosRequestConfig } from '../config/api'
import { addKey, isInToday, LocalFormatDate, today } from '../config/functions'
import { AuthUserType } from '../redux/actions/authUserActions'
import { RouteMasterType } from '../redux/actions/routeMasterActions'
import { SupplierType } from '../redux/actions/supplierActions'
import { Key } from '../redux/actions/types'
import { deleteVehicleService, VehicleServiceType } from '../redux/actions/vehicleServiceActions'
import { StoreState } from '../redux/reducers'
import { AppDispatch } from '../redux/store'

interface TableBody {
    // id?: number
    // date: string
    // vehicleId: number
    // vehicleName: string
    // supplierId: number
    // supplierName: string
    // billNumber: string
    // amount: string
    // status: VehicleServiceStatus | string
    // settledDate: string
    // createdBy?: string
    // createTimestamp?: string

    sno: number
    date: string
    routeId?: number
    routeName: string
    supplierId?: number
    supplierName: string
    paymentMode: string
    billNumber: string
    details?: string
    amount: string
    // status: VehicleServiceStatus // to be changed after api implemented
    // settledDate: string
    createdBy?: string
    createTimestamp?: string
    authUser: AuthUserType
    onEdit: MouseEventHandler<HTMLButtonElement>
    onDelete: MouseEventHandler<HTMLButtonElement>
    timeStamp: string
}

type HandleEdit = (value: VehicleServiceType) => void
type HandleDelete = (value: VehicleServiceType) => void

const VehicleService = () => {

    const toast = useToast()
    const authUser = useSelector<StoreState, AuthUserType>(state => state.authUser)
    const token = authUser.token!
    const config = getAxiosRequestConfig(token)

    const dispatch = useDispatch<AppDispatch>()

    const routeMaster = useSelector<StoreState, Array<RouteMasterType & Key>>(state => addKey(state.routeMaster))
    const supplier = useSelector<StoreState, Array<SupplierType & Key>>(state => addKey(state.supplier))

    // const VehicleService = useSelector<StoreState, Array<VehicleServiceType & Key>>(state => addKey(state.vehicleService))
    const [showForm, setShowForm] = useState<boolean>(false)
    const [formType, setFormType] = useState<FormType>(FormTypeList.ADD)
    const [editData, setEditData] = useState<VehicleServiceType>()

    const [fromDate, setFromDate] = useState<string>(today())
    const [toDate, setToDate] = useState<string>(today())
    const [routeId, setRouteId] = useState<string>('')
    const [supplierId, setSupplierId] = useState<string>('')

    const [vehicleService, setVehicleService] = useState<VehicleServiceType[]>([])

    const handleFromDate: ChangeEventHandler<HTMLInputElement> = (e) => {
        setFromDate(e.target.value)
    }

    const handleToDate: ChangeEventHandler<HTMLInputElement> = (e) => {
        setToDate(e.target.value)
    }

    const handleRouteId: ChangeEventHandler<HTMLInputElement> = (e) => {
        setRouteId(e.target.value)
    }

    const handleSupplierId: ChangeEventHandler<HTMLInputElement> = (e) => {
        setSupplierId(e.target.value)
    }

    const handleAdd = () => {
        setShowForm(true)
    }

    const handleEdit: HandleEdit = (value) => {
        setFormType(FormTypeList.UPDATE)
        setEditData(value)
        setShowForm(true)
    }

    const handleDelete: HandleDelete = (value) => {
        if (value.id !== undefined) {
            dispatch(deleteVehicleService(value.id)).then(text => {
                toast(text)
            }).catch(text => {
                toast(text, ToastTypes.ERROR)
            })
        }
    }


    const handleFormClose = () => {
        setShowForm(false)
        setFormType(FormTypeList.ADD)
        setEditData(undefined)
    }

    useEffect(() => {
        if (fromDate !== undefined && toDate !== undefined) {
            api.get<APIResponse<VehicleServiceType[]>>(`vehicle_service/filter/?from_date=${fromDate}&to_date=${toDate}&route_id=${routeId}&supplier_id=${supplierId}`, config).then(response => {
                if (response.data.data && response.status === 200) {
                    const result = response.data.data
                    setVehicleService(result)
                }
            }).catch(error => {

            })
        }
    }, [fromDate, toDate, routeId, supplierId])

    return <Fragment>
        <Modal
            headerText={formType === FormTypeList.ADD ? 'Add Vehicle Service' : 'Edit Vehicle Service'}
            visible={showForm}
            onClose={handleFormClose}
            centered
            size='lg'
        >
            <VehicleServiceForm
                formType={formType}
                editData={editData}
                onSave={handleFormClose}
            />

        </Modal>
        <VerticalLayout>
            <PageTitle
                title='Vehicle Service'
                buttonText='ADD'
                onButtonClick={handleAdd}
            />

            <div className='card'>
                <div className='card-body'>
                    <div className='container-fluid'>
                        <div className="row">
                            <div className="col-sm-6 col-md-4 col-lg-3">
                                <FormInput
                                    label='From Date'
                                    labelClassName="required"
                                    value={fromDate}
                                    onChange={handleFromDate}
                                    placeholder="From Date"
                                    containerClass="mb-2"
                                    type="date"
                                />
                            </div>
                            <div className="col-sm-6 col-md-4 col-lg-3">
                                <FormInput
                                    label='To Date'
                                    labelClassName="required"
                                    value={toDate}
                                    onChange={handleToDate}
                                    placeholder="To Date"
                                    containerClass="mb-2"
                                    type="date"
                                />
                            </div>
                            <div className="col-sm-6 col-md-4 col-lg-3">
                                <FormInput
                                    value={routeId}
                                    onChange={handleRouteId}
                                    type='select'
                                    label='Route'
                                >
                                    <option value="">All</option>
                                    {routeMaster.filter(rm => {
                                        return rm.code !== 'HIRE'
                                    }).map(rm => {
                                        return <Option value={rm.id} key={rm.key}>{rm.name}</Option>
                                    })}
                                </FormInput>
                            </div>

                            <div className="col-sm-6 col-md-4 col-lg-3">
                                <FormInput
                                    value={supplierId}
                                    onChange={handleSupplierId}
                                    type='select'
                                    label='Supplier'
                                >
                                    <option value="">All</option>
                                    {supplier.map(s => {
                                        return <Option value={s.id} key={s.key}>{s.name}</Option>
                                    })}
                                </FormInput>
                            </div>

                        </div>
                    </div>
                </div>
            </div>


            <div className="card">
                <div className="card-body">
                    <div className='table-wrapper'>
                        <table className='table colored'>
                            <thead>
                                <tr>
                                    <th className='text-truncate align-middle'>S.No</th>
                                    <th className='text-truncate align-middle'>Date</th>
                                    <th className='text-truncate align-middle'>Route</th>
                                    <th className='text-truncate align-middle'>Supplier</th>
                                    <th className='text-truncate align-middle'>Payment Mode</th>
                                    <th className='text-truncate align-middle'>Bill No</th>
                                    <th className='text-truncate align-middle'>Details</th>
                                    <th className='text-truncate align-middle'>Amount</th>
                                    {/* <th className='text-truncate align-middle'>Status</th> */}
                                    {/* <th className='text-truncate align-middle'>Settled Date</th> */}
                                    <th className='text-truncate align-middle text-center'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicleService.map((a, i) => {
                                    return <VehicleServiceBody
                                        sno={i + 1}
                                        date={LocalFormatDate(a.date)}
                                        routeName={a.routeName}
                                        supplierName={a.supplierName}
                                        paymentMode={a.paymentMode !== undefined ? a.paymentMode : ''}
                                        billNumber={a.billNumber}
                                        details={a.details}
                                        amount={a.amount}
                                        key={a.id}
                                        authUser={authUser}
                                        timeStamp={a.createTimestamp !== undefined ? a.createTimestamp : ''}
                                        onEdit={() => handleEdit(a)}
                                        onDelete={() => handleDelete(a)}
                                    />
                                })}

                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </VerticalLayout>
    </Fragment>
}

const VehicleServiceBody = ({ sno, routeName, billNumber, paymentMode, details, amount, supplierName, date, onEdit, onDelete, timeStamp, authUser }: TableBody) => {

    const ShowEditForManager = isInToday(timeStamp)

    return <tr>
        <td>{sno}</td>
        <td>{date}</td>
        <td>{routeName}</td>
        <td>{supplierName}</td>
        <td>{paymentMode}</td>
        <td>{billNumber}</td>
        <td>{details}</td>
        <td>{amount}</td>
        <td>
            <div className='hstack gap-1'>
                {(authUser.role === 'admin' || authUser.role === 'super' || authUser.role === 'Admin' || authUser.role === 'Super') && <button className='btn btn-xs btn-primary' onClick={onEdit}>Edit</button>}
                {(authUser.role === 'Manager' || authUser.role === 'manager') && ShowEditForManager && <button className='btn btn-xs btn-primary' onClick={onEdit}>Edit</button>}
                <button className='btn btn-xs btn-danger' onClick={onDelete}>Delete</button>
            </div>
        </td>
    </tr>

}

export default VehicleService
